import './index.scss'

import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import usePlaylist from '@hooks/usePlaylist';
import PageHeader from '@components/PageHeader';
import LoadingIndicator from '@components/LoadingIndicator';
import ErrorView from '@components/ErrorView';
import SongContextMenu from '@components/SongContextMenu/index';
import SongSearchModal from '@components/SharedModal/SongSearchModal';
import Playlist from '@data/interfaces/playlist.interface';
import Song from '@data/interfaces/song.interface';
import RemoveSongModal from '@pages/v2/Playlists/DetailsPage/RemoveSongModal';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';

type SongMenuState = {
  songID: string;
  x: number;
  y: number;
};

const SONG_MENU_EXIT_DURATION_MS = 140;

export default function PlaylistDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathParams = useParams();
  const playlistID = pathParams.playlistID;
  const initialPlaylist = (location.state as { playlist?: Playlist } | null)?.playlist;
  const { playlist, loading, error, hasLoaded, refetch, addSong, removeSong } = usePlaylist(playlistID, undefined, initialPlaylist);

  const [selectedSongMenu, setSelectedSongMenu] = useState<SongMenuState | null>(null);
  const [displayedSongMenu, setDisplayedSongMenu] = useState<SongMenuState | null>(null);
  const [isClosingSongMenu, setIsClosingSongMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [songPendingRemoval, setSongPendingRemoval] = useState<Song | null>(null);
  const [isRemoveSongModalOpen, setIsRemoveSongModalOpen] = useState(false);

  useEffect(() => {
    if (selectedSongMenu) {
      setDisplayedSongMenu(selectedSongMenu);
      setIsClosingSongMenu(false);
      return;
    }

    if (!displayedSongMenu) {
      setIsClosingSongMenu(false);
      return;
    }

    setIsClosingSongMenu(true);

    const timeout = window.setTimeout(() => {
      setDisplayedSongMenu(null);
      setIsClosingSongMenu(false);
    }, SONG_MENU_EXIT_DURATION_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [selectedSongMenu, displayedSongMenu]);

  if (!playlistID) {
    return <Navigate to="/playlists" replace />;
  }

  return <div className='playlist-details-page'>
    {loading && !playlist ? <LoadingIndicator /> : null}

    {error ? <ErrorView error={error} buttonText="Retry" buttonAction={() => refetch()} /> : null}

    {hasLoaded && !loading && !error && !playlist ? <ErrorView error="Playlist not found." buttonText="Back to Playlists" buttonAction={() => {
      navigate('/playlists');
    }} /> : null}

    {playlist ? <div>
      <PageHeader
        title={playlist.attributes.name}
        backLocation='/playlists' />

      <SongSearchModal
        id='add-song-to-playlist-modal'
        isOpen={showAddSongModal}
        close={() => {
          setShowAddSongModal(false);
        }}
        onConfirm={async (selection) => {
          return await addSong(selection.selectedSong);
        }} />

      <RemoveSongModal
        isOpen={isRemoveSongModalOpen}
        song={songPendingRemoval}
        close={() => {
          setIsRemoveSongModalOpen(false);
        }}
        onAfterClose={() => {
          setSongPendingRemoval(null);
        }}
        onConfirm={async (song) => {
          return await removeSong(song.id);
        }}
      />

      <div className='songs'>
        <button
          type='button'
          className='playlist-song-card add-song-card'
          onClick={() => {
            setShowAddSongModal(true);
          }}
        >
          <div className='add-song-artwork'>
            <img src={plusIcon} alt="" aria-hidden="true" />
          </div>

          <div className='song-info'>
            <h2>Add Song</h2>
          </div>
        </button>

        {playlist.songs.map((song) => {
          const activeMenu = displayedSongMenu?.songID === song.id ? displayedSongMenu : null;
          const isMenuOpen = activeMenu?.songID === song.id;

          return <div
            key={song.id}
            className={`playlist-song-card ${isMenuOpen ? 'active' : ''}`}
            onClick={(event) => {
              if (displayedSongMenu?.songID === song.id) {
                setSelectedSongMenu(null);
                return;
              }

              setSelectedSongMenu({
                songID: song.id,
                x: event.clientX + window.scrollX,
                y: event.clientY + window.scrollY
              });
            }}
            role='button'
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();

                if (displayedSongMenu?.songID === song.id) {
                  setSelectedSongMenu(null);
                  return;
                }

                const rect = event.currentTarget.getBoundingClientRect();
                setSelectedSongMenu({
                  songID: song.id,
                  x: rect.left + rect.width - 40 + window.scrollX,
                  y: rect.top + 28 + window.scrollY
                });
              }
            }}
          >
            <img
              src={song.attributes.artworkURL || ''}
              alt={song.attributes.title}
              className='cover-art'
            />

            <div className='song-info'>
              <h2>{song.attributes.title}</h2>
              <p>{song.attributes.artist.name}</p>
            </div>

            {activeMenu ? <SongContextMenu
              song={song}
              menuRef={menuRef}
              x={activeMenu.x}
              y={activeMenu.y}
              isClosing={isClosingSongMenu}
              onRemove={(selectedSong) => {
                setSelectedSongMenu(null);
                setSongPendingRemoval(selectedSong);
                setIsRemoveSongModalOpen(true);
              }}
              onRequestClose={() => {
                setSelectedSongMenu(null);
              }}
            /> : null}
          </div>
        })}
      </div>
    </div> : null}
  </div>
}
